import React, {
    lazy,
    memo,
    PropsWithChildren,
    Suspense,
    useCallback,
    MemoExoticComponent,
    useState,
    useMemo,
    useEffect,
} from 'react';
import { useApiClient } from '~/api-client';
import { getRealAuthHeaders, getRealUrlPrefix } from '~/session';
import { FederatedSearchResource, UnitResource } from '@mark43/cad-api';
import { LoadingIndicator } from '../common/components/loading-indicator';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import { ApiQueryFormResponse, FEDERATED_SEARCH_SERVICE } from 'mark43-federated-search';
import { createMfeTelemetryBridge, datadogRum } from '@mark43/datadog';
import { useBootstrapState } from '../bootstrap/bootstrap-provider';
import { AppName } from '../app-name/app-name';
import {
    FederatedQueryIdsContext,
    FederatedSearchNotificationsContext,
    FederatedSearchNotificationsContextValue,
    FederatedSearchQueryFormsContext,
    federatedSearchQueryFormsContextDefaultValue,
    FederatedSearchActionsContext,
} from './federated-search-context';
import { useGetCadEventOptions } from './hooks/use-get-cad-event-options';
import { useOpenDepartmentLink } from '../layout/use-open-department-link';
import { appNameToApplicationScope } from './helpers';
import { getDexMetadata } from '~/data-exchange/get-dex-metadata';
import { loadFederatedSearchAnchor } from './federated-search-anchor';
import { useIntegratedStreams } from '../common/hooks/use-integrated-streams';
import { useQuery } from '@tanstack/react-query';
import { FederatedSearchNotificationsSync } from './federated-search-notifications-sync';
import {
    FederatedSearchRemoteStateProvider,
    FederatedSearchRemoteState,
} from './federated-search-remote-state-context';
import { FederatedSearchApiControlFallback } from './federated-search-api-control-fallback';
import {
    isFederatedSearchUnavailable,
    lazyLoadFederatedSearchModule,
    logFederatedSearchLoadError,
    subscribeFederatedSearchUnavailable,
} from './federated-search-remote-monitor';
import { ErrorBoundary } from '../monitoring/error-boundary';

const FallbackComponent: MemoExoticComponent<
    (props: PropsWithChildren<Record<string, unknown>>) => React.JSX.Element
> = memo(({ children }) => <>{children}</>);

const FederatedSearchProvider = lazy(() =>
    import('federated-search/FederatedSearchProvider')
        .then((module) => ({
            default: module.FederatedSearchProvider,
        }))
        .catch(() => ({
            default: FallbackComponent,
        }))
);

type FederatedSearchApiControlType = typeof import('federated-search/FederatedSearchApiControl').FederatedSearchApiControl;

const FederatedSearchApiControl = lazyLoadFederatedSearchModule<FederatedSearchApiControlType>(
    'FederatedSearchApiControl',
    () => import('federated-search/FederatedSearchApiControl'),
    (module) => module.FederatedSearchApiControl as FederatedSearchApiControlType,
    FederatedSearchApiControlFallback
);

type FederatedSearchProviderProps = Parameters<typeof FederatedSearchProvider>[0];

type SubmitParams = Parameters<NonNullable<FederatedSearchProviderProps['onQueryFormSubmit']>>[0];

const notificationsDefaultValue: FederatedSearchNotificationsContextValue = {
    unreadQueryCount: 0,
    unreadQueriesIncludeSpecialResult: false,
    markAllQueriesAsRead: async () => {},
};

export const FederatedSearchProviderWrapper = ({
    children,
    appName,
}: React.PropsWithChildren<{ appName: AppName }>) => {
    const { userProfileView } = useBootstrapState();
    const { dexNextEnabled } = useEnabledFeatures();
    const apiClient = useApiClient();
    const urlPrefix = getRealUrlPrefix();
    const { getCadEventOptions } = useGetCadEventOptions();

    const getDeviceNameFallback = useCallback(async () => {
        const { deviceName } = await getDexMetadata();
        return deviceName;
    }, []);

    const openDepartmentLink = useOpenDepartmentLink();

    const openProfileLink = useCallback(
        (entityPath: string, profileId: number) => {
            openDepartmentLink({
                path: `profiles/${entityPath}/${profileId}`,
                app: 'RMS',
                tenantStrategy: 'PREFER_RMS_TENANT',
            });
        },
        [openDepartmentLink]
    );

    const [federatedSearchReadDataContextValue, setFederatedSearchReadDataContextValue] = useState(
        federatedSearchQueryFormsContextDefaultValue
    );
    const [federatedQueryIds, setFederatedQueryIds] = useState<string[]>([]);
    const [
        notificationsContextValue,
        setNotificationsContextValue,
    ] = useState<FederatedSearchNotificationsContextValue>(notificationsDefaultValue);

    const requestHeaders = useMemo(() => getRealAuthHeaders(), []);

    const getCadUnitOptions = useCallback(async () => {
        const response = await apiClient.request<UnitResource.GetAllUnits>({
            url: 'unit',
            method: 'GET',
        });
        return response.data.data?.map((unit) => ({ value: unit.id, label: unit.callSign })) || [];
    }, [apiClient]);

    const onQueryFormSubmit = useCallback(
        (querySubmission: SubmitParams) => {
            apiClient.request<FederatedSearchResource.SaveFederatedQuery>({
                method: 'POST',
                url: 'federated_search/save_federated_search_query/',
                data: {
                    federatedQueryId: querySubmission.federatedQuery.id,
                    linkToAssignedEvent: querySubmission.linkCurrentAssignedEvent,
                    queryIds:
                        querySubmission.federatedQuery.queries?.map((query) => query.id) ?? [],
                    eventId: querySubmission.contextInfo.CAD_EVENT_SELECT_VALUE,
                    unitId: querySubmission.contextInfo.CAD_UNIT_SELECT_VALUE,
                    parsedRawQuery: querySubmission.federatedQuery.parsedRawQuery,
                    queryableEntityType: querySubmission.federatedQuery.queryableEntityType,
                },
            });
        },
        [apiClient]
    );

    const federatedSearchNotificationsQuery = useQuery({
        enabled: dexNextEnabled,
        queryKey: ['federated-search-notifications'],
        queryFn() {
            return apiClient.request<FederatedSearchResource.FederatedSearchBootstrap>({
                method: 'POST',
                url: 'federated_search/bootstrap/',
            });
        },
        onSuccess(data) {
            const state = data.data.data?.federatedSearchUserQueryMetadata;
            const nextFederatedQueryIds =
                state?.federatedSearchQueryBundle?.federatedQueryIds ?? [];
            setFederatedQueryIds(nextFederatedQueryIds);
        },
    });

    useIntegratedStreams({
        enabled: dexNextEnabled,
        streamNames: ['federatedSearchUserQueryMetadata'],
        query: federatedSearchNotificationsQuery,
        onMessage(message) {
            if (message.payload.refetch) {
                federatedSearchNotificationsQuery.refetch();
            }
        },
    });

    const handleQueryFormsLoaded = useCallback((queryForms: ApiQueryFormResponse[]) => {
        setFederatedSearchReadDataContextValue((prev) => ({
            ...prev,
            queryForms,
        }));
    }, []);

    // Bridges federated-search's telemetry calls into CAD's own already-initialized Datadog RUM
    // instance — federated-search never calls datadogRum.init() itself. See
    const telemetry = useMemo(
        () => createMfeTelemetryBridge(datadogRum, FEDERATED_SEARCH_SERVICE),
        []
    );

    // Two internal signals: `anchorResolved` is sticky (set once, never unset) and
    // decides whether the federated subtree is mounted; `remoteState` is the tri-state value
    // ("available", "checking" or "unavailable") exposed to consumers. Keying the mount on
    // the sticky flag keeps the already-loaded subtree in place when a late import failure
    // flips `remoteState` to 'unavailable', so `children` (the entire app) is never remounted.
    const [anchorResolved, setAnchorResolved] = useState(false);
    const [remoteState, setRemoteState] = useState<FederatedSearchRemoteState>('checking');

    useEffect(() => subscribeFederatedSearchUnavailable(() => setRemoteState('unavailable')), []);

    useEffect(
        function checkFederatedSearchAvailability() {
            if (!dexNextEnabled) {
                return;
            }
            loadFederatedSearchAnchor()
                .then(() => {
                    setAnchorResolved(true);
                    if (!isFederatedSearchUnavailable()) {
                        setRemoteState('available');
                    }
                })
                .catch((error) => {
                    setRemoteState('unavailable');
                    logFederatedSearchLoadError('FederatedSearchProvider (pre-flight)', error);
                });
        },
        [dexNextEnabled]
    );

    if (!dexNextEnabled) {
        return <>{children}</>;
    }

    return (
        <FederatedSearchQueryFormsContext.Provider value={federatedSearchReadDataContextValue}>
            <FederatedQueryIdsContext.Provider value={federatedQueryIds}>
                <FederatedSearchNotificationsContext.Provider value={notificationsContextValue}>
                    <FederatedSearchRemoteStateProvider federatedSearchRemoteState={remoteState}>
                        {anchorResolved ? (
                            <Suspense fallback={<LoadingIndicator />}>
                                <FederatedSearchProvider
                                    requestHeaders={requestHeaders}
                                    onQueryFormSubmit={onQueryFormSubmit}
                                    userProfile={userProfileView}
                                    urlPrefix={urlPrefix}
                                    getCadUnitOptions={getCadUnitOptions}
                                    getCadEventOptions={getCadEventOptions}
                                    onQueryFormsLoaded={handleQueryFormsLoaded}
                                    applicationScope={appNameToApplicationScope(appName)}
                                    getDeviceNameFallback={getDeviceNameFallback}
                                    openProfileLink={openProfileLink}
                                    telemetry={telemetry}
                                >
                                    <ErrorBoundary
                                        name="FEDERATED_SEARCH_NOTIFICATIONS_SYNC"
                                        extraTags={{ feature: 'federated-search' }}
                                        fallback={() => null}
                                    >
                                        <Suspense fallback={null}>
                                            <FederatedSearchNotificationsSync
                                                onUpdate={setNotificationsContextValue}
                                            />
                                        </Suspense>
                                    </ErrorBoundary>
                                    <FederatedSearchApiControl>
                                        {({
                                            setSelectedForm,
                                            setQueryFormValues,
                                            setQueryFormDataToPrefill,
                                        }) => (
                                            <FederatedSearchActionsContext.Provider
                                                value={{
                                                    setSelectedForm,
                                                    setQueryFormValues,
                                                    setQueryFormDataToPrefill,
                                                }}
                                            >
                                                {children}
                                            </FederatedSearchActionsContext.Provider>
                                        )}
                                    </FederatedSearchApiControl>
                                </FederatedSearchProvider>
                            </Suspense>
                        ) : (
                            <>{children}</>
                        )}
                    </FederatedSearchRemoteStateProvider>
                </FederatedSearchNotificationsContext.Provider>
            </FederatedQueryIdsContext.Provider>
        </FederatedSearchQueryFormsContext.Provider>
    );
};
